import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { db } from "../lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import { addToCart } from "../utils/cart";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Star, ShieldCheck, ChevronRight, Minus, Plus, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { normalizeProduct, handleImageError } from "../utils/product";
import ProductIngredients from "../components/product/ProductIngredients";
import { getDiscountedPrice, getDiscountPercent, formatPrice } from '../utils/priceUtils';

const ProductDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [accepted, setAccepted] = useState({ sales: false, refund: false, privacy: false });
  const [activeImage, setActiveImage] = useState("");

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const ref = doc(db, "products", id);
        const snap = await getDoc(ref);

        if (snap.exists()) {
          const normalized = normalizeProduct(snap);
          console.log("Product detail data:", normalized); // Debug log
          setProduct(normalized);
          setActiveImage(normalized.image);
        } else {
          console.warn("Product not found:", id);
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  if (loading) return <div className="h-screen flex items-center justify-center text-fecos-gray">{t("product.loading")}</div>;
  if (!product) return <div className="h-screen flex items-center justify-center text-fecos-gray">{t("product.notFound")}</div>;

  const readableCategory = product.category?.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  // Access images from _original data or create array from single image
  const galleryImages = product._original?.images || (product.image ? [product.image] : []);

  return (
    <div className="bg-white min-h-screen pb-24">
      <ToastContainer position="top-center" autoClose={2000} hideProgressBar newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="colored" />

      {/* Breadcrumb */}
      <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-4 md:py-6 text-xs font-medium text-fecos-gray flex items-center gap-2 overflow-x-auto whitespace-nowrap">
        <Link to="/" className="hover:text-fecos-text transition-colors">{t("product.home")}</Link>
        <ChevronRight className="w-3 h-3 flex-shrink-0" />
        <Link to={`/category/${product.category}`} className="hover:text-fecos-text transition-colors">{readableCategory}</Link>
        <ChevronRight className="w-3 h-3 flex-shrink-0" />
        <span className="text-fecos-text truncate">{product.name}</span>
      </div>

      <div className="max-w-[1200px] mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-24">

        {/* Left: Image (Sticky) */}
        <div className="relative">
          <div className="sticky top-32">
            <div className="bg-fecos-bg rounded-[32px] aspect-square flex items-center justify-center p-8 lg:p-16 overflow-hidden mb-6">
              <AnimatePresence mode="wait">
                <motion.img
                  key={activeImage}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  src={activeImage}
                  alt={product.name}
                  className="w-full h-full object-contain mix-blend-multiply drop-shadow-xl"
                  onError={handleImageError}
                />
              </AnimatePresence>
            </div>

            {/* Thumbnail Gallery */}
            {galleryImages.length > 1 && (
              <div className="flex gap-4 overflow-x-auto pb-2">
                {galleryImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(img)}
                    className={`w-20 h-20 rounded-xl bg-fecos-bg p-2 border-2 transition-all ${activeImage === img ? 'border-fecos-accent' : 'border-transparent hover:border-gray-300'}`}
                  >
                    <img
                      src={img}
                      alt={`Thumbnail ${idx}`}
                      className="w-full h-full object-contain mix-blend-multiply"
                      onError={handleImageError}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: Info */}
        <div className="flex flex-col justify-center py-4 space-y-8">

          <div className="space-y-4">
            <div className="flex items-center gap-2 text-yellow-500 text-sm font-medium">
              <div className="flex"><Star className="fill-current w-4 h-4" /><Star className="fill-current w-4 h-4" /><Star className="fill-current w-4 h-4" /><Star className="fill-current w-4 h-4" /><Star className="fill-current w-4 h-4" /></div>
              <span className="text-fecos-gray">(128 {t("product.reviews")})</span>
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold text-fecos-text tracking-tight">{product.name}</h1>

            <div className="flex items-center gap-4">
              {product.discount && product.discount.isActive ? (
                <>
                  <div className="flex flex-col">
                    <span className="text-lg text-gray-400 line-through decoration-red-500/50 decoration-2">{formatPrice(product.price)}</span>
                    <span className="text-4xl lg:text-5xl font-black text-red-600 tracking-tight">{formatPrice(getDiscountedPrice(product))}</span>
                  </div>
                  <div className="bg-red-100 text-red-600 px-3 py-1 rounded-lg text-sm font-bold uppercase tracking-wider animate-pulse">
                    {t('common.salediscount', 'İndirim')} %{getDiscountPercent(product)}
                  </div>
                </>
              ) : (
                <p className="text-4xl lg:text-5xl font-semibold text-fecos-accent">{formatPrice(product.price)}</p>
              )}
            </div>
          </div>

          <div className="prose prose-sm text-fecos-text/80 leading-relaxed">
            <p>{product.description}</p>
          </div>

          {/* Controls */}
          <div className="space-y-6 pt-4 border-t border-fecos-border/50">

            {/* Quantity */}
            <div className="flex items-center gap-4">
              <span className="text-sm font-bold text-fecos-text uppercase tracking-wider">{t("product.quantity")}</span>
              <div className="flex items-center bg-fecos-bg rounded-full border border-fecos-border/50">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-10 flex items-center justify-center hover:bg-white rounded-full transition-colors"><Minus className="w-4 h-4 text-fecos-text" /></button>
                <span className="w-8 text-center text-sm font-medium">{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} className="w-10 h-10 flex items-center justify-center hover:bg-white rounded-full transition-colors"><Plus className="w-4 h-4 text-fecos-text" /></button>
              </div>
            </div>

            {/* Agreements */}
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className={`w-5 h-5 rounded border flex items-center justify-center mt-0.5 transition-colors ${accepted.sales ? 'bg-fecos-accent border-fecos-accent' : 'border-gray-300 group-hover:border-fecos-accent'}`}>
                  {accepted.sales && <Check className="w-3.5 h-3.5 text-white" />}
                </div>
                <input type="checkbox" className="hidden" checked={accepted.sales} onChange={() => setAccepted({ ...accepted, sales: !accepted.sales })} />
                <div className="text-xs text-fecos-gray leading-snug">
                  <a href="/documents/satis-sozlesmesi.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-fecos-text underline decoration-fecos-gray/50 hover:decoration-fecos-text transition-colors" onClick={(e) => e.stopPropagation()}>
                    {t("product.agreements.sales")}
                  </a>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <div className={`w-5 h-5 rounded border flex items-center justify-center mt-0.5 transition-colors ${accepted.refund ? 'bg-fecos-accent border-fecos-accent' : 'border-gray-300 group-hover:border-fecos-accent'}`}>
                  {accepted.refund && <Check className="w-3.5 h-3.5 text-white" />}
                </div>
                <input type="checkbox" className="hidden" checked={accepted.refund} onChange={() => setAccepted({ ...accepted, refund: !accepted.refund })} />
                <div className="text-xs text-fecos-gray leading-snug">
                  <a href="/documents/iade-ve-iptal.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-fecos-text underline decoration-fecos-gray/50 hover:decoration-fecos-text transition-colors" onClick={(e) => e.stopPropagation()}>
                    {t("product.agreements.refund")}
                  </a>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <div className={`w-5 h-5 rounded border flex items-center justify-center mt-0.5 transition-colors ${accepted.privacy ? 'bg-fecos-accent border-fecos-accent' : 'border-gray-300 group-hover:border-fecos-accent'}`}>
                  {accepted.privacy && <Check className="w-3.5 h-3.5 text-white" />}
                </div>
                <input type="checkbox" className="hidden" checked={accepted.privacy} onChange={() => setAccepted({ ...accepted, privacy: !accepted.privacy })} />
                <div className="text-xs text-fecos-gray leading-snug">
                  <a href="/documents/gizlilik-politikasi.pdf" target="_blank" rel="noopener noreferrer" className="hover:text-fecos-text underline decoration-fecos-gray/50 hover:decoration-fecos-text transition-colors" onClick={(e) => e.stopPropagation()}>
                    {t("product.agreements.privacy")}
                  </a>
                </div>
              </label>
            </div>

            {/* Actions */}
            <div className="flex gap-4">
              <button
                onClick={() => {
                  const allAccepted = accepted.sales && accepted.refund && accepted.privacy;
                  if (!allAccepted) return toast.error(t("common.error")); // Should be disabled anyway but fallback
                  addToCart({ id: product.id, name: product.name, price: product.price, image: product.image, quantity });
                  toast.success(t("common.success"));
                }}
                disabled={!(accepted.sales && accepted.refund && accepted.privacy)}
                className={`flex-1 py-4 rounded-full font-bold text-sm tracking-wide transition-all ${accepted.sales && accepted.refund && accepted.privacy ? 'bg-fecos-text text-white hover:bg-black hover:scale-[1.02] shadow-lg' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
              >
                {t("product.addToCart")}
              </button>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-2 gap-4 pt-6">
              <div className="flex items-center gap-3 p-4 bg-fecos-bg rounded-2xl col-span-2">
                <ShieldCheck className="w-5 h-5 text-fecos-text/60" />
                <div className="text-xs font-medium text-fecos-text/80">{t("product.securePayment")}<br /><span className="text-fecos-gray">{t("product.sslEncrypted")}</span></div>
              </div>
            </div>

            {/* Ingredients & Usage Panel */}
            <div className="pt-6">
              <ProductIngredients product={product} />
            </div>

          </div>

        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
